// ============================================================
// Datei: EditVolunteerModal.jsx
// PURPOSE:
// Bearbeiten von Libellen:
// - Vorname
// - Nachname
// - Rolle (HELFER / SUPERHELFER)
// - Schichtzuweisung / Freigabe
//
// ÄNDERUNGEN:
// - Tages-Sortierung an Tabelle day_of_deployment angepasst
// - Aufbau:   Samstag → Sonntag → Montag → Dienstag → Mittwoch → Donnerstag → Freitag
// - Festival: Freitag → Samstag → Sonntag
// - Abbau:    Sonntag → Montag → Dienstag → Mittwoch → Donnerstag → Freitag
// - Sortierung berücksichtigt jetzt zuerst Phase, dann Tag, dann Startzeit
// - "Ohne Ressort" bei bekannten Schichten bleibt behoben:
//   Untergruppen werden zuerst über shiftId erkannt
// - Zugewiesene Schichten zeigen jetzt ebenfalls Ressort / Untergruppe an
// - forceAdminShiftAccess ergänzt: Kasse-Adminansicht lädt alle Schichten
// ============================================================

import React, { useEffect, useState, useMemo, useCallback } from "react";
import Api from "../Api";
import Toast from "./Toast";
import "../styles/editForm.css";

// ============================================================
// Rollen-Konstanten
// WICHTIG:
// Diese IDs müssen zur Tabelle role passen
// 3 = SUPERHELFER
// 4 = HELFER
// ============================================================
const ROLE_ID_SUPERHELFER = 3;
const ROLE_ID_HELFER = 4;

// ============================================================
// SCHICHT-GRUPPEN
//
// Wichtig:
// - Diese Gruppen werden primär über shiftId erkannt.
// - Dadurch ist das Dropdown stabil, auch wenn das Backend bei
//   einzelnen Endpunkten kein departmentName mitsendet.
// ============================================================
const SHIFT_GROUPS = [
	{
		key: "kasse-eingang",
		label: "Kasse: Eingangskasse",
		departmentName: "Kasse",
		minShiftId: 201,
		maxShiftId: 207,
		order: 200
	},
	{
		key: "kasse-vip",
		label: "Kasse: VIP-Kasse",
		departmentName: "Kasse",
		minShiftId: 211,
		maxShiftId: 216,
		order: 210
	},
	{
		key: "chai-kuchen-kaffee",
		label: "Chai: Kuchen & Kaffee",
		departmentName: "Chai",
		minShiftId: 301,
		maxShiftId: 307,
		order: 300
	},
	{
		key: "chai-crepes",
		label: "Chai: Crêpes",
		departmentName: "Chai",
		minShiftId: 311,
		maxShiftId: 317,
		order: 310
	},
	{
		key: "food-festival",
		label: "Food: Festival",
		departmentName: "Food",
		minShiftId: 401,
		maxShiftId: 419,
		order: 400
	},
	{
		key: "food-zmorge",
		label: "Food: Zmorge",
		departmentName: "Food",
		minShiftId: 420,
		maxShiftId: 429,
		order: 420
	},
	{
		key: "food-aufbau",
		label: "Food: Aufbau",
		departmentName: "Food",
		minShiftId: 430,
		maxShiftId: 459,
		order: 430
	},
	{
		key: "food-abbau",
		label: "Food: Abbau",
		departmentName: "Food",
		minShiftId: 460,
		maxShiftId: 479,
		order: 460
	},
	{
		key: "programm-backstage",
		label: "Programm: Backstagebetreuung",
		departmentName: "Programm",
		minShiftId: 900,
		maxShiftId: 919,
		order: 900
	},
	{
		key: "programm-kanal-k",
		label: "Programm: Kanal K",
		departmentName: "Programm",
		minShiftId: 920,
		maxShiftId: 949,
		order: 920
	},
	{
		key: "kommunikation-fotografen",
		label: "Kommunikation: Fotograf:innen",
		departmentName: "Kommunikation",
		minShiftId: 950,
		maxShiftId: 959,
		order: 950
	}
];

// ============================================================
// PHASEN-SORTIERUNG GEMÄSS DATENBANK
//
// day_of_deployment:
// Aufbau:
// 11 Samstag, 12 Sonntag, 13 Montag, 14 Dienstag,
// 15 Mittwoch, 16 Donnerstag, 17 Freitag
//
// Festival:
// 21 Freitag, 22 Samstag, 23 Sonntag
//
// Abbau:
// 31 Sonntag, 32 Montag, 33 Dienstag,
// 34 Mittwoch, 35 Donnerstag, 36 Freitag
// ============================================================
const PHASE_ORDER = {
	Aufbau: 1,
	Festival: 2,
	Abbau: 3
};

const DAY_ORDER_BY_PHASE = {
	Aufbau: {
		Samstag: 1,
		Sonntag: 2,
		Montag: 3,
		Dienstag: 4,
		Mittwoch: 5,
		Donnerstag: 6,
		Freitag: 7
	},
	Festival: {
		Freitag: 1,
		Samstag: 2,
		Sonntag: 3
	},
	Abbau: {
		Sonntag: 1,
		Montag: 2,
		Dienstag: 3,
		Mittwoch: 4,
		Donnerstag: 5,
		Freitag: 6
	}
};

// ============================================================
// TEXT NORMALISIEREN
// ============================================================
const normalizeText = (value) => {
	return String(value || "").trim();
};

const normalizeLower = (value) => {
	return normalizeText(value).toLowerCase();
};

const normalizePhase = (value) => {
	const normalized = normalizeText(value).toLowerCase();

	if (normalized === "aufbau") {
		return "Aufbau";
	}

	if (normalized === "festival") {
		return "Festival";
	}

	if (normalized === "abbau") {
		return "Abbau";
	}

	return "";
};

// ============================================================
// ZEIT IN MINUTEN
// ============================================================
const toMinutes = (time) => {
	if (!time || typeof time !== "string") {
		return 0;
	}

	const [hours, minutes] = time.split(":").map(Number);

	return (hours || 0) * 60 + (minutes || 0);
};

// ============================================================
// Department extrahieren
//
// Wichtig:
// - Diese Funktion ist bewusst robust.
// - Unterschiedliche Backend-DTOs können verschiedene Feldnamen haben.
// ============================================================
const getDepartmentName = (shift) => {
	if (!shift) {
		return null;
	}

	if (typeof shift.departmentName === "string" && shift.departmentName.trim()) {
		return shift.departmentName.trim();
	}

	if (typeof shift.department === "string" && shift.department.trim()) {
		return shift.department.trim();
	}

	if (
		shift.departmentDto &&
		typeof shift.departmentDto.department === "string" &&
		shift.departmentDto.department.trim()
	) {
		return shift.departmentDto.department.trim();
	}

	if (
		shift.departmentDto &&
		typeof shift.departmentDto.name === "string" &&
		shift.departmentDto.name.trim()
	) {
		return shift.departmentDto.name.trim();
	}

	if (
		shift.departmentDTO &&
		typeof shift.departmentDTO.department === "string" &&
		shift.departmentDTO.department.trim()
	) {
		return shift.departmentDTO.department.trim();
	}

	if (
		shift.departmentDTO &&
		typeof shift.departmentDTO.name === "string" &&
		shift.departmentDTO.name.trim()
	) {
		return shift.departmentDTO.name.trim();
	}

	if (
		shift.department &&
		typeof shift.department.department === "string" &&
		shift.department.department.trim()
	) {
		return shift.department.department.trim();
	}

	if (
		shift.department &&
		typeof shift.department.name === "string" &&
		shift.department.name.trim()
	) {
		return shift.department.name.trim();
	}

	return null;
};

// ============================================================
// Shift-ID robust lesen
// ============================================================
const getShiftIdValue = (shift) => {
	if (!shift) {
		return null;
	}

	if (shift.shiftId != null) {
		return Number(shift.shiftId);
	}

	if (shift.shift_id != null) {
		return Number(shift.shift_id);
	}

	if (shift.id != null) {
		return Number(shift.id);
	}

	return null;
};

// ============================================================
// Tag robust lesen
// ============================================================
const getDayValue = (shift) => {
	if (!shift) {
		return "";
	}

	return shift.dayOfDeployment || shift.day || shift.deploymentDay || "";
};

// ============================================================
// Startzeit robust lesen
// ============================================================
const getStartTimeValue = (shift) => {
	if (!shift) {
		return "";
	}

	return shift.startTime || shift.start || "";
};

// ============================================================
// Endzeit robust lesen
// ============================================================
const getEndTimeValue = (shift) => {
	if (!shift) {
		return "";
	}

	return shift.endTime || shift.end || "";
};

// ============================================================
// Phase robust lesen
// ============================================================
const getPhaseValue = (shift) => {
	if (!shift) {
		return "";
	}

	return normalizePhase(
		shift.phase ||
		shift.shiftPhase ||
		shift.deploymentPhase ||
		shift.period ||
		""
	);
};

// ============================================================
// Falls Phase im DTO fehlt:
// Phase anhand der shiftId grob ableiten.
// Das ist nur ein Fallback. Primär soll das Backend phase liefern.
// ============================================================
const getPhaseValueWithFallback = (shift) => {
	const directPhase = getPhaseValue(shift);

	if (directPhase) {
		return directPhase;
	}

	const shiftId = getShiftIdValue(shift);

	if (shiftId === null) {
		return "Festival";
	}

	// Falls das Backend keine phase liefert, bleibt Festival der sichere
	// Standard für normale Festival-Schichten.
	return "Festival";
};

// ============================================================
// Sortierwert für Phase
// ============================================================
const getPhaseSortValue = (shift) => {
	const phase = getPhaseValueWithFallback(shift);

	return PHASE_ORDER[phase] || 99;
};

// ============================================================
// Sortierwert für Tag abhängig von Phase
// ============================================================
const getDaySortValue = (shift) => {
	const phase = getPhaseValueWithFallback(shift);
	const day = getDayValue(shift);

	return DAY_ORDER_BY_PHASE[phase]?.[day] || 99;
};

// ============================================================
// ZEITÜBERSCHNEIDUNG
// Prüft, ob zwei Schichten am gleichen Tag zeitlich kollidieren.
// Die Phase wird zusätzlich berücksichtigt, damit z. B.
// Aufbau-Samstag nicht mit Festival-Samstag kollidiert.
// ============================================================
const isOverlapping = (a, b) => {
	const phaseA = getPhaseValueWithFallback(a);
	const phaseB = getPhaseValueWithFallback(b);

	const dayA = getDayValue(a);
	const dayB = getDayValue(b);

	if (phaseA !== phaseB || dayA !== dayB) {
		return false;
	}

	const aStart = toMinutes(getStartTimeValue(a));
	let aEnd = toMinutes(getEndTimeValue(a));

	const bStart = toMinutes(getStartTimeValue(b));
	let bEnd = toMinutes(getEndTimeValue(b));

	if (aEnd <= aStart) {
		aEnd += 1440;
	}

	if (bEnd <= bStart) {
		bEnd += 1440;
	}

	return aStart < bEnd && bStart < aEnd;
};

// ============================================================
// Angefragte Anzahl robust lesen
// ============================================================
const getRequestedAmountValue = (shift) => {
	if (!shift) {
		return 0;
	}

	if (shift.requestedAmountOfVolunteers != null) {
		return Number(shift.requestedAmountOfVolunteers);
	}

	if (shift.requestedVolunteers != null) {
		return Number(shift.requestedVolunteers);
	}

	if (shift.requested != null) {
		return Number(shift.requested);
	}

	return 0;
};

// ============================================================
// Bereits gebuchte Anzahl robust lesen
// ============================================================
const getBookedAmountValue = (shift) => {
	if (!shift) {
		return 0;
	}

	if (shift.bookedShift != null) {
		return Number(shift.bookedShift);
	}

	if (shift.assignedVolunteers != null) {
		return Number(shift.assignedVolunteers);
	}

	if (shift.booked != null) {
		return Number(shift.booked);
	}

	return 0;
};

// ============================================================
// Freie Plätze berechnen
// ============================================================
const getFreeSlotsValue = (shift) => {
	return Math.max(
		0,
		getRequestedAmountValue(shift) - getBookedAmountValue(shift)
	);
};

// ============================================================
// Rolle als ID extrahieren
// ============================================================
const getRoleIdValue = (volunteer) => {
	if (!volunteer) {
		return ROLE_ID_HELFER;
	}

	if (volunteer.roleId != null) {
		return Number(volunteer.roleId);
	}

	if (volunteer.role_id != null) {
		return Number(volunteer.role_id);
	}

	if (volunteer.role?.roleId != null) {
		return Number(volunteer.role.roleId);
	}

	if (volunteer.role?.role_id != null) {
		return Number(volunteer.role.role_id);
	}

	if (volunteer.role?.id != null) {
		return Number(volunteer.role.id);
	}

	if (typeof volunteer.role === "string") {
		if (volunteer.role.toUpperCase() === "SUPERHELFER") {
			return ROLE_ID_SUPERHELFER;
		}

		if (volunteer.role.toUpperCase() === "HELFER") {
			return ROLE_ID_HELFER;
		}
	}

	if (typeof volunteer.role?.name === "string") {
		if (volunteer.role.name.toUpperCase() === "SUPERHELFER") {
			return ROLE_ID_SUPERHELFER;
		}

		if (volunteer.role.name.toUpperCase() === "HELFER") {
			return ROLE_ID_HELFER;
		}
	}

	if (typeof volunteer.role?.role === "string") {
		if (volunteer.role.role.toUpperCase() === "SUPERHELFER") {
			return ROLE_ID_SUPERHELFER;
		}

		if (volunteer.role.role.toUpperCase() === "HELFER") {
			return ROLE_ID_HELFER;
		}
	}

	if (typeof volunteer.roleName === "string") {
		if (volunteer.roleName.toUpperCase() === "SUPERHELFER") {
			return ROLE_ID_SUPERHELFER;
		}

		if (volunteer.roleName.toUpperCase() === "HELFER") {
			return ROLE_ID_HELFER;
		}
	}

	return ROLE_ID_HELFER;
};

// ============================================================
// Prüft, ob eine Schicht-ID in einem Bereich liegt
// ============================================================
const isShiftIdInRange = (shift, minShiftId, maxShiftId) => {
	const shiftId = getShiftIdValue(shift);

	if (shiftId === null) {
		return false;
	}

	return shiftId >= minShiftId && shiftId <= maxShiftId;
};

// ============================================================
// Schichtgruppe für eine Schicht bestimmen
//
// Wichtig:
// 1. ZUERST bekannte Untergruppen nur über shiftId erkennen.
// 2. Erst danach Department-Fallback verwenden.
// Dadurch gibt es bei bekannten ID-Bereichen kein "Ohne Ressort" mehr.
// ============================================================
const getShiftGroupForShift = (shift) => {
	const knownGroupByShiftId = SHIFT_GROUPS.find((group) =>
		isShiftIdInRange(shift, group.minShiftId, group.maxShiftId)
	);

	if (knownGroupByShiftId) {
		return knownGroupByShiftId;
	}

	const departmentName = getDepartmentName(shift) || "Ohne Ressort";

	return {
		key: `department-${departmentName}`,
		label: departmentName,
		departmentName,
		minShiftId: null,
		maxShiftId: null,
		order: 10000 + departmentName.localeCompare("A")
	};
};

// ============================================================
// Schichten für Dropdown gruppieren
// ============================================================
const buildShiftSelectGroups = (shifts) => {
	const groups = new Map();

	(shifts || []).forEach((shift) => {
		const group = getShiftGroupForShift(shift);

		if (!groups.has(group.key)) {
			groups.set(group.key, {
				...group,
				shifts: []
			});
		}

		groups.get(group.key).shifts.push(shift);
	});

	return Array.from(groups.values())
		.map((group) => ({
			...group,
			shifts: group.shifts.sort((a, b) => {
				const phaseDiff =
					getPhaseSortValue(a) -
					getPhaseSortValue(b);

				if (phaseDiff !== 0) {
					return phaseDiff;
				}

				const dayDiff =
					getDaySortValue(a) -
					getDaySortValue(b);

				if (dayDiff !== 0) {
					return dayDiff;
				}

				const timeDiff =
					toMinutes(getStartTimeValue(a)) -
					toMinutes(getStartTimeValue(b));

				if (timeDiff !== 0) {
					return timeDiff;
				}

				return (getShiftIdValue(a) || 0) - (getShiftIdValue(b) || 0);
			})
		}))
		.sort((a, b) => {
			if (a.order !== b.order) {
				return a.order - b.order;
			}

			return a.label.localeCompare(b.label, "de", {
				sensitivity: "base"
			});
		});
};

// ============================================================
// Option-Text für Dropdown
//
// Zweck:
// - Dieses Label wird im Dropdown und als Basis für zugewiesene
//   Schichten verwendet.
// ============================================================
const getShiftOptionLabel = (shift) => {
	const phase = getPhaseValue(shift);
	const day = getDayValue(shift);
	const start = getStartTimeValue(shift);
	const end = getEndTimeValue(shift);
	const freeSlots = getFreeSlotsValue(shift);

	const phasePrefix = phase ? `${phase} · ` : "";

	return `${phasePrefix}${day} ${start}–${end} (${freeSlots} frei)`;
};

// ============================================================
// Label für bereits zugewiesene Schichten
//
// Zweck:
// - Bereits zugewiesene Schichten sollen ebenfalls zeigen,
//   zu welchem Ressort / welcher Untergruppe sie gehören.
// - Beispiel:
//   "Kasse: Eingangskasse · Freitag 16:00–18:00 (0 frei)"
// ============================================================
const getAssignedShiftLabel = (shift) => {
	const group = getShiftGroupForShift(shift);
	const baseLabel = getShiftOptionLabel(shift);

	return `${group.label} · ${baseLabel}`;
};

const EditVolunteerModal = ({
	volunteer,
	okDepartment = "",
	forceAdminShiftAccess = false,
	onShiftChanged,
	onClose,
	onSaved
}) => {
	// ============================================================
	// BASISDATEN
	// ============================================================
	const [firstName, setFirstName] = useState("");
	const [lastName, setLastName] = useState("");
	const [roleId, setRoleId] = useState(ROLE_ID_HELFER);

	// ============================================================
	// SCHICHTEN
	// ============================================================
	const [assignedShifts, setAssignedShifts] = useState([]);
	const [allShifts, setAllShifts] = useState([]);
	const [selectedShiftId, setSelectedShiftId] = useState("");

	// WICHTIG:
	// Dieser State merkt sich, ob die geladenen Schichten bereits
	// vom Backend auf das Ressort eingeschränkt wurden.
	// Dann darf das Frontend NICHT nochmals auf Department filtern.
	const [isDepartmentScopedSource, setIsDepartmentScopedSource] = useState(false);

	// ============================================================
	// UI
	// ============================================================
	const [showToast, setShowToast] = useState(false);
	const [loading, setLoading] = useState(false);

	// ============================================================
	// eingeloggtes Department
	// ============================================================
	const loggedInDepartment = useMemo(() => {
		const dept = localStorage.getItem("department");
		return dept || okDepartment || "";
	}, [okDepartment]);

	// ============================================================
	// eingeloggte Rolle
	// ============================================================
	const loggedInRole = useMemo(() => {
		return (localStorage.getItem("role") || "").toUpperCase();
	}, []);

	// ============================================================
	// Prüfen, ob der eingeloggte User ein OK-Mitglied ist
	// ============================================================
	const isOkMember = useMemo(() => {
		return loggedInRole === "OK" || loggedInRole === "ROLE_OK";
	}, [loggedInRole]);

	// ============================================================
	// Prüfen, ob der eingeloggte User Admin ist
	// ============================================================
	const isAdmin = useMemo(() => {
		return (
			forceAdminShiftAccess ||
			loggedInRole === "ADMIN" ||
			loggedInRole === "ROLE_ADMIN"
		);
	}, [forceAdminShiftAccess, loggedInRole]);

	// ============================================================
	// ZUGEWIESENE SCHICHTEN LADEN
	// ============================================================
	const loadAssignedShifts = useCallback(async () => {
		if (!volunteer?.volunteerId) {
			return;
		}

		try {
			const res = await Api.get(
				`/deployments/volunteers/${volunteer.volunteerId}/shifts`
			);

			setAssignedShifts(Array.isArray(res.data) ? res.data : []);
		} catch (e) {
			console.error("Fehler beim Laden der zugewiesenen Schichten", e);
			setAssignedShifts([]);
		}
	}, [volunteer]);

	// ============================================================
	// ALLE SCHICHTEN LADEN
	//
	// ADMIN:
	// - zuerst /admin/shifts, damit Department-Daten vollständiger sind
	// - Fallback: /shifts
	//
	// OK:
	// - bevorzugt Ressort-Endpunkt
	// - dort kommen bereits nur die Schichten dieses Ressorts
	//
	// Fallback:
	// - /shifts
	// ============================================================
	const loadShifts = useCallback(async () => {
		try {
			if (isAdmin) {
				try {
					const adminRes = await Api.get("/admin/shifts");
					setAllShifts(Array.isArray(adminRes.data) ? adminRes.data : []);
					setIsDepartmentScopedSource(false);
					return;
				} catch (adminEndpointError) {
					console.warn(
						"Admin-Schicht-Endpunkt konnte nicht geladen werden, Fallback auf /shifts:",
						adminEndpointError
					);
				}

				const fallbackRes = await Api.get("/shifts");
				setAllShifts(Array.isArray(fallbackRes.data) ? fallbackRes.data : []);
				setIsDepartmentScopedSource(false);
				return;
			}

			if (isOkMember && loggedInDepartment) {
				try {
					const encodedDepartment = encodeURIComponent(loggedInDepartment);
					const res = await Api.get(
						`/deployments/department/${encodedDepartment}/shifts`
					);

					setAllShifts(Array.isArray(res.data) ? res.data : []);
					setIsDepartmentScopedSource(true);
					return;
				} catch (departmentEndpointError) {
					console.warn(
						"Ressort-Endpunkt konnte nicht geladen werden, Fallback auf /shifts:",
						departmentEndpointError
					);
				}
			}

			const res = await Api.get("/shifts");
			setAllShifts(Array.isArray(res.data) ? res.data : []);
			setIsDepartmentScopedSource(false);
		} catch (e) {
			console.error("Fehler beim Laden der Schichten", e);
			setAllShifts([]);
			setIsDepartmentScopedSource(false);
		}
	}, [isAdmin, isOkMember, loggedInDepartment]);

	// ============================================================
	// INITIAL LOAD
	// ============================================================
	useEffect(() => {
		if (!volunteer) {
			return;
		}

		setFirstName(volunteer.firstName || "");
		setLastName(volunteer.lastName || "");
		setRoleId(getRoleIdValue(volunteer));

		loadAssignedShifts();
		loadShifts();
	}, [volunteer, loadAssignedShifts, loadShifts]);

	// ============================================================
	// VERFÜGBARE SCHICHTEN
	//
	// Filter:
	// - Admin: keine Department-Einschränkung
	// - OK: nur eigenes Department, falls Quelle nicht schon gefiltert ist
	// - freie Plätze
	// - keine schon zugewiesenen
	// - keine zeitlich überlappenden
	//
	// WICHTIG:
	// Es wird NICHT nach "öffentlich" gefiltert.
	// ============================================================
	const availableShifts = useMemo(() => {
		return allShifts
			.filter((shift) => {
				const shiftId = getShiftIdValue(shift);
				const department = getDepartmentName(shift);

				if (!shiftId) {
					return false;
				}

				if (
					!isAdmin &&
					isOkMember &&
					loggedInDepartment &&
					!isDepartmentScopedSource
				) {
					if (!department) {
						return false;
					}

					if (normalizeLower(department) !== normalizeLower(loggedInDepartment)) {
						return false;
					}
				}

				const freeSlots = getFreeSlotsValue(shift);

				if (freeSlots <= 0) {
					return false;
				}

				const alreadyAssigned = assignedShifts.some(
					(assigned) => getShiftIdValue(assigned) === shiftId
				);

				if (alreadyAssigned) {
					return false;
				}

				const overlaps = assignedShifts.some((assigned) =>
					isOverlapping(assigned, shift)
				);

				if (overlaps) {
					return false;
				}

				return true;
			})
			.sort((a, b) => {
				const groupA = getShiftGroupForShift(a);
				const groupB = getShiftGroupForShift(b);

				if (groupA.order !== groupB.order) {
					return groupA.order - groupB.order;
				}

				const phaseDiff =
					getPhaseSortValue(a) -
					getPhaseSortValue(b);

				if (phaseDiff !== 0) {
					return phaseDiff;
				}

				const dayDiff =
					getDaySortValue(a) -
					getDaySortValue(b);

				if (dayDiff !== 0) {
					return dayDiff;
				}

				const timeDiff =
					toMinutes(getStartTimeValue(a)) -
					toMinutes(getStartTimeValue(b));

				if (timeDiff !== 0) {
					return timeDiff;
				}

				return (getShiftIdValue(a) || 0) - (getShiftIdValue(b) || 0);
			});
	}, [
		allShifts,
		assignedShifts,
		isAdmin,
		isOkMember,
		loggedInDepartment,
		isDepartmentScopedSource
	]);

	// ============================================================
	// GRUPPIERTE SCHICHTEN FÜR DAS SELECT
	// ============================================================
	const shiftSelectGroups = useMemo(() => {
		return buildShiftSelectGroups(availableShifts);
	}, [availableShifts]);

	// ============================================================
	// NAME + ROLLE SPEICHERN
	// ============================================================
	const handleSaveBaseData = async (e) => {
		e.preventDefault();
		setLoading(true);

		try {
			await Api.put(`/volunteers/${volunteer.volunteerId}`, {
				firstName,
				lastName,
				dateOfBirth: volunteer?.dateOfBirth ?? null,
				roleId: Number(roleId)
			});

			setShowToast(true);
			onSaved?.();
		} catch (error) {
			console.error("Fehler beim Speichern", error);
			alert("Fehler beim Speichern");
		} finally {
			setLoading(false);
		}
	};

	// ============================================================
	// SCHICHT ZUWEISEN
	// ============================================================
	const handleAssignShift = async () => {
		if (!selectedShiftId) {
			return;
		}

		try {
			await Api.post(
				`/deployments/shifts/${selectedShiftId}/enroll/${volunteer.volunteerId}`
			);

			setSelectedShiftId("");
			await loadAssignedShifts();
			await loadShifts();

			onShiftChanged?.();
			onSaved?.();
		} catch (error) {
			console.error("Schicht konnte nicht zugewiesen werden", error);
			alert("Schicht konnte nicht zugewiesen werden");
		}
	};

	// ============================================================
	// SCHICHT FREIGEBEN
	// ============================================================
	const handleRemoveShift = async (shiftId) => {
		try {
			await Api.delete(
				`/deployments/shifts/${shiftId}/unenroll/${volunteer.volunteerId}`
			);

			await loadAssignedShifts();
			await loadShifts();

			onShiftChanged?.();
			onSaved?.();
		} catch (error) {
			console.error("Schicht konnte nicht freigegeben werden", error);
			alert("Schicht konnte nicht freigegeben werden");
		}
	};

	// ============================================================
	// SCHLIESSEN
	// ============================================================
	const handleClose = () => {
		onSaved?.();
		onClose?.();
	};

	return (
		<>
			{showToast && (
				<Toast
					message="Änderungen gespeichert"
					onClose={() => setShowToast(false)}
				/>
			)}

			<div className="modal-backdrop modal-overlay-top-left">
				<div className="register-form dark-glass">
					<h3 className="editform-heading">
						Libelle bearbeiten
					</h3>

					<form onSubmit={handleSaveBaseData} className="editform-form">
						<input
							className="editform-input"
							value={firstName}
							onChange={(e) => setFirstName(e.target.value)}
							required
						/>

						<input
							className="editform-input"
							value={lastName}
							onChange={(e) => setLastName(e.target.value)}
							required
						/>

						<select
							className="editform-input"
							value={roleId}
							onChange={(e) => setRoleId(Number(e.target.value))}
						>
							<option value={ROLE_ID_HELFER}>
								Helfer
							</option>

							<option value={ROLE_ID_SUPERHELFER}>
								Superhelfer
							</option>
						</select>

						<button
							className="editform-action-button"
							type="submit"
							disabled={loading}
						>
							Speichern
						</button>
					</form>

					<h4 className="editform-subtitle">
						Zugewiesene Schicht(en)
					</h4>

					{assignedShifts.length === 0 && (
						<p className="editform-hint">
							Keine Schichten zugewiesen
						</p>
					)}

					<ul className="assigned-shift-list">
						{assignedShifts.map((shift) => (
							<li
								key={getShiftIdValue(shift)}
								className="assigned-shift-item"
							>
								<span>
									{getAssignedShiftLabel(shift)}
								</span>

								<button
									type="button"
									className="editform-secondary-button"
									onClick={() => handleRemoveShift(getShiftIdValue(shift))}
								>
									Freigeben
								</button>
							</li>
						))}
					</ul>

					<h4 className="editform-subtitle">
						Schicht hinzufügen
					</h4>

					<div className="assign-shift-row">
						<select
							className="editform-input"
							value={selectedShiftId}
							onChange={(e) => setSelectedShiftId(e.target.value)}
						>
							<option value="">
								Schicht auswählen
							</option>

							{shiftSelectGroups.map((group) => (
								<optgroup
									key={group.key}
									label={group.label}
								>
									{group.shifts.map((shift) => {
										const shiftId = getShiftIdValue(shift);

										return (
											<option
												key={shiftId}
												value={shiftId}
											>
												{getShiftOptionLabel(shift)}
											</option>
										);
									})}
								</optgroup>
							))}
						</select>

						<button
							type="button"
							className="editform-action-button"
							onClick={handleAssignShift}
							disabled={!selectedShiftId}
						>
							+ Zuweisen
						</button>
					</div>

					<div className="modal-buttons">
						<button
							type="button"
							className="editform-secondary-button"
							onClick={handleClose}
						>
							Schliessen
						</button>
					</div>
				</div>
			</div>
		</>
	);
};

export default EditVolunteerModal;